import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { KeyRound, TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'
import { api, ApiError } from '@/lib/api'
import { AuthLayout } from '@/components/AuthLayout'
import { Button, Field, PasswordInput } from '@/components/ui'
import { KaproLogo } from '@/components/Logo'
import { useT } from '@/i18n/I18nProvider'

/**
 * Where the link in the recovery e-mail lands.
 *
 * The query carries `token` and `sifra` — the token and the employee code. Not
 * the e-mail address: Laravel's stock reset link puts the address in the query
 * string, and it then sits in browser history and in any Referer the page leaks.
 * The employee code identifies the same account, is already public inside the
 * plant, and lets this screen say *whose* password is being changed, which the
 * address would not do any better.
 *
 * A missing or malformed link is handled here rather than by letting the form
 * fail on submit: someone whose mail client mangled the URL should be told to
 * request a new one before they choose a password and type it twice.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const t = useT()

  const token = searchParams.get('token') ?? ''
  const employeeCode = searchParams.get('sifra') ?? ''
  // Set by the invitation mail. Cosmetic only — the server decides which token
  // is valid, so a hand-added flag changes nothing but the greeting.
  const isInvitation = searchParams.get('pozivnica') === '1'

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    // Caught here so the mismatch lands on the fields instead of arriving as a
    // toast after a round trip. The server checks it too.
    if (password !== confirm) {
      setError(t('profile.password.mismatch'))
      return
    }

    setSubmitting(true)

    try {
      await api.post('/auth/reset-password', {
        employeeCode,
        token,
        password,
        password_confirmation: confirm,
      })

      toast.success(t('profile.reset.success'))

      /*
       * Deliberately not signed in automatically.
       *
       * An account with two-factor enabled would otherwise have its second
       * factor bypassed by anyone holding the mailbox, which is exactly the
       * situation the second factor exists for. Going through the front door
       * costs one more form and keeps every check in place.
       *
       * The code is carried across so the field arrives filled.
       */
      navigate(`/prijava?sifra=${encodeURIComponent(employeeCode)}`, { replace: true })
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.fieldError('token') ?? err.fieldError('password') ?? err.message)
          : t('profile.reset.failed'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (!token || !employeeCode) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center text-center">
          <KaproLogo size="lg" />

          <span className="mt-6 flex size-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
            <TriangleAlert className="size-6" aria-hidden />
          </span>

          <h1 className="mt-4 text-xl font-semibold">{t('profile.reset.badLink.title')}</h1>

          <p className="muted mt-2 text-sm">{t('profile.reset.badLink.body')}</p>
        </div>

        <Button
          fullWidth
          className="mt-6"
          render={<Link to="/prijava/zaboravljena-lozinka" />}
          nativeButton={false}
        >
          {t('profile.reset.badLink.action')}
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center">
        <KaproLogo size="lg" />

        {/* Same treatment as the login screen: whitespace only, and only on a
            viewport too short to afford it. */}
        <span className="bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300 mt-6 flex size-12 items-center justify-center rounded-2xl [@media(max-height:700px)]:mt-4">
          <KeyRound className="size-6" aria-hidden />
        </span>

        {/*
          One screen, two occasions.

          An invitation and a reset are the same act — prove you control the
          address, then choose a password — so they share this form rather than
          having a second screen doing the same job with its own bugs. Only the
          wording changes, because "obnovite lozinku" makes no sense to somebody
          who has never had one.
        */}
        <h1 className="mt-4 text-xl font-semibold">
          {t(isInvitation ? 'profile.reset.welcome' : 'profile.reset.title')}
        </h1>

        {/*
          The code is interpolated rather than set in bold between two halves
          of the sentence, which is what it used to be. It sits mid-sentence,
          and where mid-sentence is differs in all four languages — so the
          bold would have cost three fragments a translator cannot reorder.
        */}
        <p className="muted mt-2 text-sm">
          {t(isInvitation ? 'profile.reset.intro.invitation' : 'profile.reset.intro.reset', {
            code: employeeCode,
          })}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-8 space-y-4 [@media(max-height:700px)]:mt-5"
        noValidate
      >
        {/* Hidden, but present and filled: password managers refuse to offer to
            save a credential when they cannot see which account it belongs to. */}
        <input type="hidden" name="username" autoComplete="username" value={employeeCode} />

        <Field
          label={t('profile.password.new')}
          required
          hint={t('profile.password.new.hint')}
        >
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            autoFocus
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'reset-error' : undefined}
          />
        </Field>

        <Field label={t('profile.password.confirm')} required>
          <PasswordInput
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            enterKeyHint="go"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'reset-error' : undefined}
          />
        </Field>

        {error && (
          <p
            id="reset-error"
            role="alert"
            className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-300"
          >
            {error}
          </p>
        )}

        <Button type="submit" fullWidth loading={submitting}>
          {t('profile.reset.submit')}
        </Button>
      </form>

      <p className="muted mt-5 text-center text-xs [@media(max-height:700px)]:mt-3">
        {t('profile.reset.allSessionsNote')}
      </p>

      <div className="mt-4 text-center [@media(max-height:700px)]:mt-2">
        <Link
          to="/prijava"
          className="muted min-h-11 text-sm underline-offset-4 hover:underline"
        >
          {t('profile.backToLogin')}
        </Link>
      </div>
    </AuthLayout>
  )
}
